/**
 * DiagramPage.tsx — the vertical slice.
 *
 * Open a diagram, place a symbol, connect two ports, insert a component into a
 * line, see what the Engine resolved, validate, review, publish, and reload the
 * new canonical revision. Every one of those steps goes through the same path:
 *
 *   gesture → intent → authoring/preview → (user approves) → authoring/apply
 *           → change set → submit → approve → publish → new projection
 *
 * The loop closes on the server. After an apply, the client does not patch its
 * own picture: it re-reads the diagram and renders what the Engine now says the
 * diagram is. That is slower than optimistic rendering and it is the only way
 * the drawing on screen is guaranteed to be the drawing in the repository.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useClient } from "../../api/ClientContext";
import { DiagramCanvas, type ConnectIntent, type MoveIntent, type PlacementIntent } from "../../canvas/DiagramCanvas";
import type { Hit } from "../../canvas/hitTest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Panel } from "../../components/Panel";
import { disciplineDisplay } from "../../disciplines/registry";
import { useAsync } from "../../stores/useAsync";
import type { AuthoringOperationInput, SymbolCatalogEntry } from "../../types/contract";
import { AuthoringPreviewPanel } from "../authoring/AuthoringPreviewPanel";
import * as authoring from "../authoring/operations";
import { ChangeSetPanel, ConflictNotice } from "../changeSets/ChangeSetPanel";
import { useChangeSet } from "../changeSets/useChangeSet";
import { SymbolPalette } from "../symbols/SymbolPalette";
import { ValidationPanel } from "../validation/ValidationPanel";
import { Inspector } from "./Inspector";
import { ObjectField } from "./ObjectField";

export function DiagramPage({ projectId, diagramId }: { projectId: string; diagramId: string }): JSX.Element {
  const client = useClient();
  const changeSets = useChangeSet(projectId);

  const diagram = useAsync(() => client.readDiagram(projectId, diagramId), [client, projectId, diagramId]);
  const catalog = useAsync(() => client.symbolCatalog(projectId), [client, projectId]);
  const validation = useAsync(() => client.validate(projectId, diagramId), [client, projectId, diagramId]);

  const [armed, setArmed] = useState<SymbolCatalogEntry | undefined>(undefined);
  const [objectId, setObjectId] = useState("");
  const [selection, setSelection] = useState<Hit>({ kind: "none" });
  const [pending, setPending] = useState<AuthoringOperationInput[] | undefined>(undefined);
  const [applied, setApplied] = useState(false);
  const [live, setLive] = useState(false);

  // §10.4 — the server tells us when the project moves. A revision that changed
  // under an open diagram is exactly the case optimistic rendering would hide.
  useEffect(() => {
    return client.subscribeEvents(projectId, event => {
      if (event.kind === "project.revision.changed") diagram.reload();
    }, setLive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId]);

  const view = diagram.data;
  const base = useMemo(
    () => (view ? { diagramId: view.diagramId, revision: view.revision } : undefined),
    [view],
  );

  /** Propose operations: preview first, always. Nothing applies without a look. */
  const propose = useCallback(async (operations: AuthoringOperationInput[]) => {
    setApplied(false);
    setPending(operations);
    await changeSets.previewOperations(operations, base);
  }, [base, changeSets]);

  const applyPending = useCallback(async () => {
    if (!pending) return;
    const result = await changeSets.applyOperations(pending, base);
    // THE ACCEPTANCE FLAG, NOT `applied`.
    //
    // `applied` is documented as ALWAYS false on this route: recording is working
    // state inside the change set, and only publication writes canonical state. So
    // reading `result.applied` as the success condition reported EVERY successful
    // placement as a failure — the panel never left its "Engine preview" state, the
    // diagram was never re-read, and the user was told nothing happened right after
    // the Engine had accepted their gesture. A unit test built on a fixture that
    // returned `applied: true` could never have found it, which is the argument for
    // running the real thing.
    //
    // Acceptance is the single required boolean the result schema declares — the
    // same field the preview reads, read here by shape rather than by name, so this
    // screen holds no engineering vocabulary.
    if (result === undefined || result.ok !== true) return;
    setApplied(true);
    setPending(undefined);
    setArmed(undefined);
    // Re-read rather than patch: the Engine's projection is the picture.
    diagram.reload();
    validation.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, changeSets, pending]);

  const onPlace = useCallback((intent: PlacementIntent) => {
    if (!view) return;
    if (!objectId.trim()) return;
    void propose([authoring.placeAt({
      diagramId: view.diagramId,
      defId: intent.defId,
      objectId: objectId.trim(),
      atMm: intent.atMm,
      ...(armed?.dependency ? { symbolDependency: armed.dependency.alias } : {}),
    })]);
  }, [armed, objectId, propose, view]);

  const onConnect = useCallback((intent: ConnectIntent) => {
    if (!view) return;
    void propose([authoring.connectPorts({ diagramId: view.diagramId, from: intent.from, to: intent.to })]);
  }, [propose, view]);

  const onMove = useCallback((intent: MoveIntent) => {
    if (!view) return;
    void propose([authoring.moveTo(view.diagramId, intent.symbolId, intent.toMm)]);
  }, [propose, view]);

  const onRotate = useCallback((symbolId: string) => {
    if (!view) return;
    const symbol = view.projection.symbols.find(entry => entry.id === symbolId);
    if (!symbol) return;
    const next = ((symbol.rotation + 90) % 360) as 0 | 90 | 180 | 270;
    void propose([authoring.rotate(view.diagramId, symbolId, next)]);
  }, [propose, view]);

  const onRemove = useCallback((symbolId: string) => {
    if (!view) return;
    void propose([authoring.remove(view.diagramId, symbolId)]);
  }, [propose, view]);

  /**
   * Insert into a line: one operation, not four.
   *
   * The armed palette entry says WHAT to insert; the selected trace says WHERE.
   * Everything else — retiring the original, carrying its line number and
   * fluid onto both halves, routing them — is the Engine's, atomically.
   */
  const onInsertInline = useCallback((traceId: string) => {
    if (!view || !armed || !objectId.trim()) return;
    void propose([authoring.insertInline({
      diagramId: view.diagramId,
      traceId,
      defId: armed.defId,
      objectId: objectId.trim(),
    })]);
  }, [armed, objectId, propose, view]);

  const selectedSymbolId = selection.kind === "symbol" || selection.kind === "port" ? selection.symbol.id : undefined;
  const discipline = view ? disciplineDisplay(view.diagramType) : undefined;

  return (
    <div className="workbench">
      <header className="workbench-head">
        <div>
          <h1>{view?.title ?? diagramId}</h1>
          <p className="muted">
            {discipline ? `${discipline.label} · ` : ""}
            {view ? `${view.symbolCount} symbols · ${view.traceCount} traces · revision ${view.revision}` : "loading…"}
            {view ? ` · canonical ${view.canonicalRevision.slice(0, 12)}` : ""}
          </p>
        </div>
        <span className={live ? "live is-on" : "live"}>{live ? "live" : "offline"}</span>
      </header>

      <ErrorBanner error={diagram.error} onDismiss={diagram.reload} />
      <ErrorBanner error={changeSets.error} onDismiss={changeSets.clearError} />
      {changeSets.conflict && (
        <ConflictNotice
          conflict={changeSets.conflict}
          onReload={() => { changeSets.clearError(); diagram.reload(); }}
          onDismiss={changeSets.clearError}
        />
      )}

      <div className="workbench-body">
        <aside className="workbench-left">
          <SymbolPalette
            entries={catalog.data}
            armedDefId={armed?.defId}
            onArm={setArmed}
            disabled={changeSets.busy}
          />
          <ObjectField
            projectId={projectId}
            value={objectId}
            onChange={setObjectId}
            required={armed !== undefined}
          />
        </aside>

        <main className="workbench-canvas">
          {view ? (
            <DiagramCanvas
              projection={view.projection}
              pendingDefId={armed && objectId.trim() ? armed.defId : undefined}
              selectedId={selectedSymbolId}
              readOnly={changeSets.busy}
              onSelect={setSelection}
              onPlace={onPlace}
              onConnect={onConnect}
              onMove={onMove}
            />
          ) : (
            <div className="canvas-placeholder">{diagram.loading ? "Loading the diagram…" : "No diagram."}</div>
          )}
          {view && view.projection.unresolved.length > 0 && (
            <Panel title="Unresolved" tone="warn">
              <ul className="findings">
                {view.projection.unresolved.map(entry => (
                  <li key={`${entry.kind}-${entry.id}`} className="severity-warning">
                    <code>{entry.kind} {entry.id}</code><span>{entry.reason}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </main>

        <aside className="workbench-right">
          <Inspector
            hit={selection}
            readOnly={changeSets.busy}
            onRotate={onRotate}
            onRemove={onRemove}
            {...(armed && objectId.trim() ? { onInsertInline } : {})}
          />
          <AuthoringPreviewPanel
            preview={changeSets.preview}
            busy={changeSets.busy}
            applied={applied}
            {...(pending ? { onApply: () => void applyPending() } : {})}
            onDiscard={() => { changeSets.clearPreview(); setPending(undefined); setApplied(false); }}
          />
          <ChangeSetPanel controller={changeSets} />
          <ValidationPanel summary={validation.data} onRefresh={validation.reload} busy={validation.loading} />
        </aside>
      </div>
    </div>
  );
}
